// The one place that decides whether a request to Django carries the Auth0 bearer, so
// every client that talks to Django sends the same credential, and the one way an Auth0
// session ends.
import { datadogRum } from '@datadog/browser-rum';

import { getAuth0State, whenAuth0Api, whenAuth0Settled, type Auth0Api } from './auth0State';
import { auth0LogoutUrl, auth0PostLogoutUri, isAuth0Configured } from './config';
import {
    clearAuth0Session,
    clearAuth0TenantSession,
    hasAuth0Session,
    hasAuth0TenantSession,
    isAuth0SessionMarkedHere,
} from './session';
import { NO_ACCOUNT_DETAIL } from './signInError';
import { Auth0StaffSessionEndedError, isAuth0StaffImpersonation } from './staffImpersonation';

import { localViews } from 'modules/Auth/AuthProvider/views';

/**
 * The SDK could not produce a token for a session that may still be live: a lock timeout,
 * a network error, a 5xx or 429 from the token endpoint, an SDK that failed to load. The
 * request fails; nobody is signed out.
 */
export class Auth0TokenUnavailableError extends Error {
    constructor(cause: unknown) {
        super('The Auth0 access token is unavailable', { cause });
        this.name = 'Auth0TokenUnavailableError';
    }
}

// The SDK's answers that mean the session itself is over. Anything else says nothing about
// the session, so it must not end in a sign-out: the request fails and the next one tries
// again. Null for these sends the request bare, and the backend's 401 signs the member out
// the usual way.
const SESSION_ENDED_ERRORS = new Set([
    'login_required',
    'consent_required',
    'interaction_required',
    'invalid_grant',
    'missing_refresh_token',
    'mfa_required',
    'access_denied',
]);

type CacheMode = 'on' | 'off';

const inFlight: Partial<Record<CacheMode, Promise<string | null>>> = {};

// A Django session in this browser: a full one, or the ephemeral token of a sign-in
// waiting on its MFA code. Either takes precedence over an Auth0 session.
export function hasDjangoCredential(): boolean {
    const token = localViews.token();

    return Boolean(token?.access || token?.ephemeral_token);
}

function usesAuth0Bearer(): boolean {
    // Django first: a Django credential present alongside an Auth0 session was issued later
    // -- a sign-in, a magic link, staff impersonating a member, MFA's ephemeral token -- or is
    // the one a registration exchange started from, and either way is the identity that
    // applies. The markers, not the SDK alone: a deployed build keeps the SDK's tokens per
    // tab, so another tab's sign-out reaches this one only through the markers it clears.
    //
    // A session this tab marked also carries on while the tenant marker stays. A restore that
    // fails in another tab clears only the app marker, but this tab's refresh token outlives
    // the tenant session that restore needed. A sign-out does clear the tenant marker: while
    // it is set, every sign-out runs endAuth0Session.
    return (
        isAuth0Configured() &&
        !hasDjangoCredential() &&
        (hasAuth0Session() || (isAuth0SessionMarkedHere() && hasAuth0TenantSession()))
    );
}

/**
 * Reloads the page when another tab signed in with Auth0 after this tab's SDK settled without a
 * session, and says whether it did. A deployed build keeps the SDK's session in the tab that
 * signed in, so this tab cannot use it, and must not take it for a failed restore of its own:
 * clearing the marker, or ending the tenant session, would sign the other tab out too. After
 * the reload the store comes from shared storage and the SDK restores the tenant session.
 */
export function adoptAnotherTabsSignIn(): boolean {
    const { isLoading, isAuthenticated, error, loadFailed } = getAuth0State();
    // A marker this tab does not know of: while its SDK has no session, only another tab's
    // sign-in writes one.
    const signedInElsewhere =
        usesAuth0Bearer() && !isLoading && !isAuthenticated && !error && !loadFailed && !isAuth0SessionMarkedHere();

    if (signedInElsewhere) {
        window.location.reload();
    }

    return signedInElsewhere;
}

function isSessionEnded(error: unknown): boolean {
    const code = typeof error === 'object' && error !== null && 'error' in error ? error.error : undefined;

    return typeof code === 'string' && SESSION_ENDED_ERRORS.has(code);
}

async function fetchAuth0Token(cacheMode: CacheMode): Promise<string | null> {
    try {
        const api = await whenAuth0Api();

        return await api.getAccessTokenSilently(cacheMode === 'off' ? { cacheMode } : undefined);
    } catch (error) {
        datadogRum.addError(error instanceof Error ? error : new Error(String(error)), {
            source: 'auth0.getAccessToken',
        });

        if (isSessionEnded(error)) {
            return null;
        }
        throw new Auth0TokenUnavailableError(error);
    }
}

// `applies` says whether this browser's Auth0 session is the one asked for.
async function requestAuth0Token(refresh: boolean, applies: () => boolean): Promise<string | null> {
    if (!applies()) {
        return null;
    }

    await whenAuth0Settled();
    // Again: a sign-in or sign-out, here or in another tab, can land while the SDK settles.
    if (!applies()) {
        return null;
    }

    const state = getAuth0State();
    if (!state.isAuthenticated) {
        // An SDK that failed to load or start cannot say whether the session is over. The
        // failure is reported where it happened.
        if (state.loadFailed || state.error) {
            throw new Auth0TokenUnavailableError(state.error);
        }
        // Not sent bare: its 401 would force a logout that ends the other tab's session.
        if (adoptAnotherTabsSignIn()) {
            throw new Auth0TokenUnavailableError(new Error('Another tab signed in; reloading'));
        }
        return null;
    }

    // One SDK call per cache mode, however many requests ask at once. Each call on an expiring
    // token queues on the SDK's lock, and every caller still waiting after five seconds fails.
    const cacheMode: CacheMode = refresh ? 'off' : 'on';
    let pending = inFlight[cacheMode];
    if (!pending) {
        pending = fetchAuth0Token(cacheMode).finally(() => {
            inFlight[cacheMode] = undefined;
        });
        inFlight[cacheMode] = pending;
    }

    return pending;
}

/**
 * The Auth0 access token, or null when the request should use the Django path: Auth0 is
 * not configured, a Django credential exists, this browser holds no Auth0 session, or that
 * session has ended. `refresh` skips the SDK's cache. Throws Auth0TokenUnavailableError
 * when a live session's token cannot be had right now.
 */
export function auth0AccessToken(options?: { refresh?: boolean }): Promise<string | null> {
    return requestAuth0Token(Boolean(options?.refresh), usesAuth0Bearer);
}

/** auth0AccessToken as an Authorization header value. */
export async function auth0AuthorizationHeader(options?: { refresh?: boolean }): Promise<string | null> {
    const token = await auth0AccessToken(options);

    return token ? `Bearer ${token}` : null;
}

// The staffer's Auth0 session, which the member's Django token takes precedence over as the bearer.
function holdsAuth0StaffSession(): boolean {
    return isAuth0Configured() && (hasAuth0Session() || (isAuth0SessionMarkedHere() && hasAuth0TenantSession()));
}

/**
 * The X-Staff-Token-While-Impersonate value for an impersonation started from an Auth0 session:
 * the staffer's Auth0 token, never the member's. Null when there is no such impersonation.
 * Throws Auth0StaffSessionEndedError once that session is over, and Auth0TokenUnavailableError
 * when its token cannot be had right now.
 */
export async function auth0StaffImpersonationHeader(): Promise<string | null> {
    if (!isAuth0StaffImpersonation()) {
        return null;
    }

    const token = await requestAuth0Token(false, holdsAuth0StaffSession);
    if (!token) {
        throw new Auth0StaffSessionEndedError();
    }

    return `Bearer ${token}`;
}

/**
 * Renews an Auth0 bearer the backend answered with a 401, skipping the SDK's cache, and says
 * whether one retry is worth sending. Not when the backend says the identity has no Collective
 * account, which no new token changes. A token the SDK cannot produce right now throws
 * Auth0TokenUnavailableError, as it does before sending: that says nothing about the session.
 */
export async function renewRejectedAuth0Bearer(responseBody: unknown): Promise<boolean> {
    const detail =
        typeof responseBody === 'object' && responseBody !== null && 'detail' in responseBody
            ? responseBody.detail
            : undefined;
    if (detail === NO_ACCOUNT_DETAIL) {
        return false;
    }

    return Boolean(await auth0AuthorizationHeader({ refresh: true }));
}

// Long enough for a lazily loaded SDK chunk on a slow connection, short enough that a sign-out
// never looks stuck.
const SDK_LOGOUT_TIMEOUT_MS = 10_000;

// The SDK's methods, or a rejection once it is clear they are not coming.
function whenAuth0ApiWithin(ms: number): Promise<Auth0Api> {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('The Auth0 SDK was not ready to sign out')), ms);
        whenAuth0Api()
            .then(resolve, reject)
            .finally(() => clearTimeout(timer));
    });
}

/**
 * Ends this browser's Auth0 session: clears both markers, then leaves for the tenant's logout,
 * which returns to /login. Never only locally: a tenant session left live signs the next /login
 * straight back in. Without the SDK -- it failed to load, or is not up within
 * SDK_LOGOUT_TIMEOUT_MS -- it goes to the same endpoint by URL. Never rejects.
 *
 * `replaceHistory` leaves with a replace rather than a push, for a page that must not be
 * revisited: Back to it would run whatever sent it here again.
 */
export async function endAuth0Session(options?: { replaceHistory?: boolean }): Promise<void> {
    clearAuth0TenantSession();
    clearAuth0Session();

    const leave = (url: string) => {
        if (options?.replaceHistory) {
            window.location.replace(url);
        } else {
            window.location.assign(url);
        }
    };

    try {
        const api = await whenAuth0ApiWithin(SDK_LOGOUT_TIMEOUT_MS);
        // Without openUrl the SDK navigates to /v2/logout itself, with a push.
        await api.logout({
            logoutParams: { returnTo: auth0PostLogoutUri() },
            ...(options?.replaceHistory ? { openUrl: leave } : {}),
        });
    } catch (error) {
        datadogRum.addError(error instanceof Error ? error : new Error(String(error)), {
            source: 'auth0.logout',
        });
        leave(auth0LogoutUrl());
    }
}
